import {
  WittyAssistantChatRequest,
  WittyAssistantChatResponse,
} from '@agilelab/plugin-wb-witty-common';
import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';
import { ResponseError } from '@mainstage/errors';

export type WittySessionSummary = {
  session_id: string;
  title: string;
  created_at: string | null;
  updated_at: string | null;
};

export type WittyHistoryMessage = {
  type: 'human' | 'ai';
  content: string;
};

export type WittyAssistantApi = {
  chat(
    request: WittyAssistantChatRequest,
    onToken?: (token: string) => void,
    onStatus?: (status: string) => void,
  ): Promise<WittyAssistantChatResponse>;

  getSessions(
    limit?: number,
    offset?: number,
    search?: string,
  ): Promise<WittySessionSummary[]>;

  getHistory(sessionId: string): Promise<WittyHistoryMessage[]>;

  renameSession(sessionId: string, title: string): Promise<void>;

  deleteSession(sessionId: string): Promise<void>;
};

type WittyStreamDoneEvent = {
  session_id?: string;
};

type WittyStreamTokenEvent = {
  content?: string;
};

type WittyStreamStatusEvent = {
  content?: string;
  message?: string;
};

const parseSseEvent = (rawEvent: string) => {
  let eventName = 'message';
  const dataLines: string[] = [];

  for (const line of rawEvent.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return {
    eventName,
    payload: JSON.parse(dataLines.join('\n')),
  };
};

export class WittyAssistantClient implements WittyAssistantApi {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  async chat(
    request: WittyAssistantChatRequest,
    onToken?: (token: string) => void,
    onStatus?: (status: string) => void,
  ): Promise<WittyAssistantChatResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');

    const response = await this.fetchApi.fetch(`${baseUrl}/v1/chat`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    if (!response.body) {
      throw new Error('Missing witty stream response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = '';
    let message = '';
    let sessionId = request.request.session_id ?? '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });

      let separatorIndex = buffer.indexOf('\n\n');
      while (separatorIndex !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);

        const parsed = parseSseEvent(rawEvent);
        if (parsed) {
          if (parsed.eventName === 'token') {
            const token =
              (parsed.payload as WittyStreamTokenEvent).content ?? '';
            if (token) {
              message += token;
              onToken?.(token);
            }
          }

          if (
            parsed.eventName === 'status' ||
            parsed.eventName === 'thinking'
          ) {
            const statusPayload = parsed.payload as WittyStreamStatusEvent;
            const statusMsg =
              statusPayload.content ?? statusPayload.message ?? '';
            if (statusMsg) {
              onStatus?.(statusMsg);
            }
          }

          if (parsed.eventName === 'done') {
            sessionId =
              (parsed.payload as WittyStreamDoneEvent).session_id ?? sessionId;
          }

          if (parsed.eventName === 'error') {
            throw new Error(
              (parsed.payload as { message?: string }).message ??
                'Witty streaming failed',
            );
          }
        }

        separatorIndex = buffer.indexOf('\n\n');
      }

      if (done) {
        break;
      }
    }

    return {
      message,
      session_id: sessionId,
      result: message,
    };
  }

  async getSessions(
    limit = 10,
    offset = 0,
    search?: string,
  ): Promise<WittySessionSummary[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/sessions?limit=${limit}&offset=${offset}${searchParam}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data = await response.json();
    return data.sessions ?? [];
  }

  async getHistory(sessionId: string): Promise<WittyHistoryMessage[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/history/${encodeURIComponent(sessionId)}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    const data = await response.json();
    return data.messages ?? [];
  }

  async renameSession(sessionId: string, title: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/sessions/${encodeURIComponent(sessionId)}/title`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE' },
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }
}

export const wittyAssistantApiRef: ApiRef<WittyAssistantApi> =
  createApiRef<WittyAssistantApi>({
    id: 'wb-witty-assistant',
  });
